<template>
  <div class="comment-input-container">
    <FriedAvatar
      v-if="currentUser.name"
      class="avatar"
      :name="currentUser.name"
      :thumb-url="isTemporary ? '' : currentUser.thumbUrl"
      :size="AvatarSize.Small"
    />
    <div class="input-controls">
      <FriedFieldSet class="input-box">
        <div ref="editorContainer" class="editor-container">
          <MentionParticipants
            v-if="showMentionParticipants"
            :vertical-offset="mentionListVerticalOffset"
            :mention-participants="mentionParticipants"
            :selected-index="selectedIndex"
            @add-mention="addMention"
            @close="hideMentionParticipants"
          />
          <EditorContent
            class="editor-input"
            :data-external="dataExternal"
            :editor="editor"
            spellcheck="true"
            @keydown.down="handlePressDown"
            @keydown.up="handlePressUp"
            @keydown.enter="handleEnterPress"
            @keydown.escape="handleEscapePress"
          />
        </div>
      </FriedFieldSet>
      <div class="buttons">
        <LinkButton
          :disabled="isTemporary"
          is-bold
          class="discard"
          :text="t('cc_cancel_button')"
          @click.stop.prevent="$emit('discard')"
        />
        <FriedFloater
          v-if="emojiIndex"
          :main-axis-offset="16"
          :cross-axis-offset="2"
          :show="showEmojis"
          class="floater-emoji-picker"
          :placement="Placement.BottomStart"
        >
          <template #reference>
            <FriedIconButton
              data-external="comment-input-emoji-button"
              :disabled="isTemporary"
              @click="setShowEmojiPicker(!showEmojis)"
            >
              <FriedIcon :aria-label="t('show-emoji-picker')" icon="smiley" />
            </FriedIconButton>
          </template>
          <FriedEmojiPicker
            v-click-outside="clickOutside"
            :localization="emojiLocalization"
            :emoji-index="emojiIndex"
            @select="handleAddEmoji"
          />
        </FriedFloater>
        <FriedIconButton
          data-external="comment-input-submit-button"
          :disabled="!hasInput"
          @click="handleSubmit"
        >
          <FriedIcon icon="send-arrow" />
        </FriedIconButton>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import {
  EnterBreakNode,
  InputDocNode,
  ParagraphNode,
  ProsemirrorHelper,
  ProsemirrorType,
  TextPlaceholder,
} from '@getaccept/editor-lib-new';
import { MentionNode } from '@getaccept/editor-lib-new/src/tiptap/nodes/mention-node';
import type { Emoji } from '@getaccept/fried-tofu';
import { AvatarSize, Placement } from '@getaccept/fried-tofu';
import History from '@tiptap/extension-history';
import Text from '@tiptap/extension-text';
import { EditorContent, Extension, Editor as TipTapEditor } from '@tiptap/vue-3';
import type { ComputedRef, PropType, Ref } from 'vue';
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import debounce from 'debounce';
import type { EmojiIndex } from 'emoji-mart-vue-fast';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { ChatHelper } from '../../chat/helpers/chat.helper';
import { EmojiHelper } from '../../emoji/helpers/emoji.helper';
import { t } from '../../helpers/translation.helper';
import LinkButton from '../components/LinkButton.vue';
import MentionParticipants from '../mentions/components/MentionParticipants.vue';
import { useMentionsKeybindings } from '../mentions/composables/mentions-keybindings.composable';
import { MentionsHelper } from '../mentions/helpers/mentions.helper';
import type { MentionParticipant } from '../mentions/types/mention-participant';
import type { CommentParticipant } from '../types/comment-participant';
import type { CurrentUser } from '../types/current-user';

export default defineComponent({
  components: {
    LinkButton,
    EditorContent,
    MentionParticipants,
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    currentUser: {
      type: Object as PropType<CurrentUser>,
      default: () => ({ name: '', thumbUrl: '' }),
    },
    mentionParticipants: {
      type: Array as PropType<MentionParticipant[]>,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: '',
    },
    isTemporary: {
      type: Boolean,
    },
    dataExternal: {
      type: String,
      default: '',
    },
    selected: {
      type: Boolean,
    },
    mentionsEnabled: {
      type: Boolean,
    },
  },
  emits: ['discard', 'input', 'submit'],
  setup(props, { emit }) {
    const editor: Ref<TipTapEditor | null> = ref(null);
    const emojiIndex: Ref<EmojiIndex> = ref(null);
    const showEmojis = ref(false);

    const hasInput = ref(!!props.value);

    const setFocus = () => {
      if (editor.value?.view.hasFocus()) {
        return;
      }
      editor.value.commands.focus();
    };

    const showMentionParticipants = ref(false);
    const participantsQuery = ref('');

    const hideMentionParticipants = () => {
      showMentionParticipants.value = false;
      participantsQuery.value = '';
      editor.value.commands.focus();
    };

    const evaluateShowMentionParticipants = () => {
      if (!props.mentionsEnabled) {
        return;
      }
      const { query = '', show } = MentionsHelper.shouldShowMentionParticipants(editor.value);
      participantsQuery.value = query;
      showMentionParticipants.value = show;
    };

    const debounceShowMentionParticipants = debounce(evaluateShowMentionParticipants, 100);

    const mentionListOffset = 4;

    watch(
      () => props.value,
      () => {
        if (editor.value?.view.hasFocus()) {
          return;
        }

        editor.value.commands.setContent(parsedContent.value);
      }
    );

    const mentionListVerticalOffset: ComputedRef<number> = computed(() => {
      if (!showMentionParticipants.value) {
        return 0;
      }
      const top = editorContainer.value?.getBoundingClientRect().height;
      return top + mentionListOffset;
    });

    const handleInput = () => {
      if (props.isTemporary) {
        return;
      }
      debounceShowMentionParticipants();
      const richText = JSON.stringify(editor.value.getJSON());
      const text = ProsemirrorHelper.getInnerTextContent(editor.value.state);
      hasInput.value = !!text?.length;

      if (richText === props.value) {
        return;
      }
      emit('input', { richText, text });
    };

    const setShowEmojiPicker = (show: boolean) => {
      showEmojis.value = show;
    };

    const handleAddEmoji = ({ native }: Emoji) => {
      setShowEmojiPicker(false);
      editor.value.commands.insertContent(native);
      setFocus();
    };

    const handleSubmit = () => {
      if (!hasInput.value) {
        return;
      }

      emit('submit', {
        richText: JSON.stringify(editor.value.getJSON()),
        text: ProsemirrorHelper.getInnerTextContent(editor.value.state),
      });

      editor.value.commands.setContent('');
      hasInput.value = false;
    };

    const clickOutside = () => {
      setShowEmojiPicker(false);
    };

    const parsedContent = computed(() => {
      // TODO: Delete all comments with plain text in DB, remove this try catch
      let content;
      try {
        content = JSON.parse(props.value);
      } catch (e) {
        content = props.value;
      }
      return content;
    });

    const shouldFocus = computed(() => props.selected && !props.isTemporary);

    onMounted(async () => {
      editor.value = new TipTapEditor({
        content: parsedContent.value,
        enablePasteRules: false,
        extensions: [
          InputDocNode,
          ParagraphNode,
          Text,
          History,
          EnterBreakNode,
          TextPlaceholder.configure({ placeholder: props.placeholder }),
          MentionNode.configure({ mentionParticipants: props.mentionParticipants }),
          Extension.create({
            addKeyboardShortcuts() {
              return {
                'Mod-Enter'() {
                  handleSubmit();
                  return true;
                },
              };
            },
          }),
        ],
        onSelectionUpdate: debounceShowMentionParticipants,
        onUpdate: handleInput,
        onCreate: () => {
          if (shouldFocus.value) {
            editor.value?.view.focus();
          }
          editor.value?.setEditable(!props.isTemporary);
        },
      });

      emojiIndex.value = await EmojiHelper.emojiIndex();
    });

    const getParticipantId = (commentParticipant: CommentParticipant) =>
      commentParticipant.recipientId || commentParticipant.userId;

    const createMentionId = (commentParticipant: CommentParticipant) => {
      const type: string = commentParticipant.recipientId ? 'recipientId' : 'userId';
      const id = getParticipantId(commentParticipant);
      return `<${type}:${id}>`;
    };

    const addMention = (commentParticipant: CommentParticipant, selectionFromOffset = 0) => {
      const { tr, schema, selection }: EditorState = editor.value.state;

      const node = schema.nodes[ProsemirrorType.Mention].create({
        ['data-mention-participant-id']: getParticipantId(commentParticipant),
        ['data-mention-id']: createMentionId(commentParticipant),
      });

      // Replace the query text and the @ symbol with the actual mention
      const selectionFrom =
        selection.from - participantsQuery.value.length - 1 - selectionFromOffset;

      const transaction: Transaction = tr.replaceRangeWith(selectionFrom, selection.to, node);
      editor.value.view.dispatch(transaction);
      hideMentionParticipants();
      editor.value.commands.focus();
    };

    onBeforeUnmount(() => {
      editor.value.destroy();
    });

    const editorContainer = ref(null);

    const filteredMentionParticipants = computed(() =>
      props.mentionParticipants.filter((mentionParticipant: MentionParticipant) =>
        mentionParticipant.fullName.toLowerCase().includes(participantsQuery.value.toLowerCase())
      )
    );

    const { handlePressUp, handlePressDown, selectedIndex } = useMentionsKeybindings(
      filteredMentionParticipants,
      showMentionParticipants
    );

    const handleEnterPress = (event: KeyboardEvent) => {
      if (!showMentionParticipants.value) {
        return;
      }
      // Enter causes one additional selection hence selectionOffset 1
      addMention(filteredMentionParticipants.value[selectedIndex.value].commentParticipant, 1);
      event.preventDefault();
    };

    const handleEscapePress = (event: KeyboardEvent) => {
      if (!showMentionParticipants.value) {
        return;
      }
      hideMentionParticipants();
      event.preventDefault();
    };

    watch(
      () => props.isTemporary,
      isTemporary => {
        if (!isTemporary) {
          editor.value?.setEditable(true);
        }
      },
      { immediate: true }
    );

    return {
      AvatarSize,
      editor,
      emojiIndex,
      showEmojis,
      emojiLocalization: ChatHelper.getEmojiLocalization(),
      hasInput,
      showMentionParticipants,
      editorContainer,
      clickOutside,
      setFocus,
      setShowEmojiPicker,
      handleAddEmoji,
      handleSubmit,
      t,
      addMention,
      hideMentionParticipants,
      mentionListVerticalOffset,
      filteredMentionParticipants,
      handlePressUp,
      handlePressDown,
      handleEnterPress,
      handleEscapePress,
      selectedIndex,
      Placement,
    };
  },
});
</script>
<style lang="scss" scoped>
/* stylelint-disable selector-class-pattern */
.floater-emoji-picker {
  :deep(.floater-container) {
    z-index: 1;
  }
}

.comment-input-container {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-50);
  margin-top: var(--spacing-100);

  .avatar {
    margin-top: var(--spacing-25);
  }

  .input-controls {
    width: 100%;
    display: flex;
    flex-direction: column;

    .input-box {
      width: 100%;

      .editor-container {
        width: 100%;
        padding: var(--spacing-50) var(--spacing-75);

        .editor-input {
          overflow-y: scroll;
          max-height: 6.125rem;
          min-height: 3rem;

          :deep(.ProseMirror) {
            outline: 0;

            p {
              font-family: var(--default-font-family);
              font-size: var(--paragraph-medium-font-size);
              font-weight: var(--paragraph-medium-font-weight);
              line-height: var(--paragraph-medium-line-height);
              margin-block-start: 0;
              margin-block-end: 0;
              overflow-wrap: anywhere;
            }

            .is-editor-empty:first-child::before {
              content: attr(data-placeholder);
              color: var(--gray-60);
              pointer-events: none;
              height: 0;
            }

            &.ProseMirror-focused {
              .is-editor-empty:first-child::before {
                content: ' ';
              }
            }
          }
        }
      }
    }

    .buttons {
      display: flex;
      justify-content: flex-end;
      position: relative;
      align-items: center;

      .discard {
        position: absolute;
        left: 0;
      }
    }
  }
}
</style>
